import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { DragOverlay } from '@dnd-kit/core';
import HierarchyNodeContent from './HierarchyNodeContent';
import { IconDragEqual } from 'svgIcons/MotionPortalIcons';

const MAX_VISIBLE_ITEMS = 5;

const HierarchyDragLayer = React.memo(({
    draggedItems,
    renderNode,
    renderIcon,
}) => {

    // Render stacked items preview
    const renderDragPreview = useMemo(() => {
        if (!draggedItems || draggedItems.length === 0) {
            return null;
        }

        const itemCount = draggedItems.length;
        const visibleItems = draggedItems.slice(0, MAX_VISIBLE_ITEMS);

        return (
            <div className='hierarchy-drag-preview'>
                <div className='hierarchy-drag-preview-stack'>
                    {visibleItems.map((item) =>
                        <div
                            key={item.id}
                            className='hierarchy-drag-preview-item'
                        >
                            {/* Render icon if provided */}
                            {renderIcon &&
                                <span className='hierarchy-drag-preview-icon'>
                                    {renderIcon(item, { isExpanded: false, level: 0 })}
                                </span>
                            }

                            {/* Render node content */}
                            {renderNode ?
                                renderNode(item, {
                                    isSelected: true,
                                    isExpanded: false,
                                    level: 0,
                                    isDragging: true,
                                })
                                :
                                <HierarchyNodeContent
                                    node={item}
                                />
                            }

                            {IconDragEqual &&
                                <span className='hierarchy-drag-preview-equal-icon'>
                                    <IconDragEqual width={12} height={12} />
                                </span>
                            }
                        </div>
                    )}
                </div>

                {/* Badge showing total count */}
                {itemCount > MAX_VISIBLE_ITEMS &&
                    <div className='hierarchy-drag-preview-badge'>
                        {itemCount}
                    </div>
                }
            </div>
        );
    }, [draggedItems, renderNode, renderIcon]);

    return (
        <DragOverlay dropAnimation={null} style={{ pointerEvents: 'none' }}>
            {renderDragPreview}
        </DragOverlay>
    );
});

HierarchyDragLayer.propTypes = {
    draggedItems: PropTypes.array,
    columns: PropTypes.array,
    renderNode: PropTypes.func,
    renderIcon: PropTypes.func,
    getNodeKey: PropTypes.func.isRequired,
};

HierarchyDragLayer.defaultProps = {
    draggedItems: [],
    columns: null,
    renderNode: null,
    renderIcon: null,
};

HierarchyDragLayer.displayName = 'HierarchyDragLayer';

export default HierarchyDragLayer;
